import React, { useState, useEffect, useRef } from 'react';
import tyroConfigService from '../../services/tyroConfigService';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { getTyroClient, resetTyroClient } from '../../utils/tyroClient';

const toCentsString = (amount) => {
  const value = Number(amount) || 0;
  const cents = Math.round((value + Number.EPSILON) * 100);
  return String(cents);
};

const normalizeTyroReceipt = (copy, label) => {
  if (!copy) return '';
  if (typeof copy === 'string') return copy;
  if (Array.isArray(copy)) {
    console.debug(`[TyroRefund] Normalizing array for ${label}`, copy);
    return copy.join('\n');
  }
  const possibleFields = [
    copy.text,
    copy.lines,
    copy.printLines,
    copy.receipt,
    copy.receiptText,
    copy.formattedReceipt,
    copy.rawReceipt,
    copy.body,
  ];
  for (const field of possibleFields) {
    if (!field) continue;
    if (typeof field === 'string') return field;
    if (Array.isArray(field)) return field.join('\n');
    if (typeof field === 'object') {
      console.debug(`[TyroRefund] Nested object field for ${label}`, field);
    }
  }
  try {
    console.debug(`[TyroRefund] Serializing fallback for ${label}`, copy);
    return JSON.stringify(copy, null, 2);
  } catch (err) {
    console.warn(`[TyroRefund] Failed to stringify receipt for ${label}`, err);
    return String(copy);
  }
};

const extractTyroReceiptCopies = (payload = {}) => {
  const assign = (value, label) => normalizeTyroReceipt(value, label);
  const customer = { current: '' };
  const merchant = { current: '' };
  const addIfEmpty = (target, value, label) => {
    if (target.current || !value) return;
    target.current = assign(value, label);
  };

  addIfEmpty(customer, payload.customerReceipt, 'customer');
  addIfEmpty(merchant, payload.merchantReceipt, 'merchant');
  addIfEmpty(customer, payload.customerReceiptText, 'customer');
  addIfEmpty(merchant, payload.merchantReceiptText, 'merchant');
  addIfEmpty(customer, payload.customerCopy, 'customer');
  addIfEmpty(merchant, payload.merchantCopy, 'merchant');

  if (Array.isArray(payload.receipts)) {
    payload.receipts.forEach((entry) => {
      const type = (entry?.type || entry?.copyType || '').toLowerCase();
      if (type.includes('customer')) {
        addIfEmpty(customer, entry, 'customer');
      } else if (type.includes('merchant')) {
        addIfEmpty(merchant, entry, 'merchant');
      }
    });
  }

  if (!customer.current && typeof payload === 'object') {
    addIfEmpty(customer, payload.customer, 'customer');
  }
  if (!merchant.current && typeof payload === 'object') {
    addIfEmpty(merchant, payload.merchant, 'merchant');
  }

  if (!customer.current && !merchant.current) {
    const fallback = assign(payload, 'receipt');
    return { customerReceipt: fallback, merchantReceipt: fallback };
  }

  return {
    customerReceipt: customer.current,
    merchantReceipt: merchant.current,
  };
};

const TyroRefundModal = ({
  total,
  onRefundSuccess,
  onRefundError,
  onCancel,
  title = 'Process Tyro Refund',
  subtitle = '',
  disableCancel = false,
}) => {
  const { activeStore } = useStore();
  const { tillId, getCurrentTillForStore } = useAuth();
  const currentTillId = activeStore?._id ? getCurrentTillForStore(activeStore._id) : tillId;
  const [tyro, setTyro] = useState(null);
  const [tyroConfig, setTyroConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [tyroPrompt, setTyroPrompt] = useState(null);
  const receiptsRef = useRef({ customerReceipt: '', merchantReceipt: '' });

  useEffect(() => {
    // Ensure a fresh Tyro SDK client whenever store/till context changes.
    resetTyroClient();
    setTyro(null);
    return () => {
      resetTyroClient();
    };
  }, [activeStore?._id, currentTillId]);

  const recordReceiptPayload = (payload) => {
    if (!payload) return;
    const hasReceiptData =
      Boolean(
        payload.customerReceipt ||
          payload.merchantReceipt ||
          payload.customerReceiptText ||
          payload.merchantReceiptText ||
          payload.customerCopy ||
          payload.merchantCopy ||
          payload.text ||
          payload.lines ||
          payload.printLines ||
          payload.receipt ||
          payload.receiptText ||
          payload.formattedReceipt ||
          payload.rawReceipt ||
          payload.body
      ) ||
      (Array.isArray(payload.receipts) && payload.receipts.length > 0);
    if (!hasReceiptData) {
      return;
    }
    const extracted = extractTyroReceiptCopies(payload);
    if (!extracted.customerReceipt && !extracted.merchantReceipt) {
      return;
    }
    if (tyroConfig?.integratedReceiptEnabled === false) {
      console.info('[TyroRefund] Integrated receipts disabled; ignoring receipt payload');
      return;
    }
    receiptsRef.current = {
      customerReceipt: extracted.customerReceipt || receiptsRef.current.customerReceipt || '',
      merchantReceipt: extracted.merchantReceipt || receiptsRef.current.merchantReceipt || '',
    };
  };

  const resetReceipts = () => {
    receiptsRef.current = { customerReceipt: '', merchantReceipt: '' };
  };

  useEffect(() => {
    let isMounted = true;
    const fetchConfigAndInitTyro = async () => {
      if (!activeStore) return;
      try {
        const config = await tyroConfigService.getTyroConfig(activeStore._id, { includeSensitive: true });
        // Refunds require the active till's pairing; do not fall back to store-level keys.
        let resolvedConfig = { ...config, tid: null, integrationKey: null, tillName: null };

        if (currentTillId) {
          try {
            const storeDetails = await storeService.getStoreById(activeStore._id);
            const matchingTill = storeDetails?.tills?.find((till) => till._id === currentTillId);
            resolvedConfig = {
              ...resolvedConfig,
              tid: matchingTill?.tid || null,
              integrationKey: matchingTill?.tyroIntegrationKey || null,
              tillName: matchingTill?.name || null,
            };
          } catch (tillError) {
            console.warn('Failed to resolve till-level Tyro settings', tillError);
          }
        }
        if (!isMounted) return;
        setTyroConfig(resolvedConfig);

        if (resolvedConfig?.apiKey) {
          const siteReference =
            [activeStore?.address?.street, activeStore?.address?.suburb, activeStore?.address?.state]
              .filter(Boolean)
              .join(', ') ||
            activeStore?.name ||
            '';
          const posProductInfo = {
            posProductVendor: 'Bizessence Pty Ltd',
            posProductName: 'BoltinPOS',
            posProductVersion: '1.0.0',
            siteReference,
          };
          try {
            const localHost = String(window?.location?.hostname || '').toLowerCase();
            const forceHeadlessForLocalhost = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(localHost);
            const preferHeadless = forceHeadlessForLocalhost ? true : resolvedConfig.useHeadlessMode !== false;
            const iClient = await getTyroClient({
              apiKey: resolvedConfig.apiKey,
              posProductInfo,
              preferHeadless,
            });
            if (isMounted) setTyro(iClient);
          } catch (clientError) {
            console.error(clientError);
            if (isMounted) setTyro(null);
          }
        } else {
          console.error('Tyro iClient library not loaded');
        }
      } catch (error) {
        console.error('Failed to fetch Tyro config', error);
      }
    };

    fetchConfigAndInitTyro();

    return () => {
      isMounted = false;
    };
  }, [activeStore, currentTillId]);

  const handleRefund = async () => {
    if (!tyro || !tyroConfig) return;
    // Fail fast if this till is not paired with Tyro.
    if (!tyroConfig.tid || !tyroConfig.integrationKey) {
      onRefundError('Tyro terminal not paired. Select a terminal and pair it in Settings -> Tyro.');
      return;
    }
    setProcessing(true);

    const { mid, tid, integrationKey, integratedReceiptEnabled } = tyroConfig;

    const requestParams = {
      amount: toCentsString(total),
      integratedReceipt: integratedReceiptEnabled !== false,
      mid: parseInt(mid),
      tid: parseInt(tid),
      integrationKey,
    };

    const isHeadless = tyroConfig?.useHeadlessMode !== false;

    const transactionCallbacks = {
      questionCallback: isHeadless
        ? (question, answerCallback) => {
            console.info('Tyro refund question received', question);
            if (typeof answerCallback !== 'function') return;
            const options = Array.isArray(question?.options) && question.options.length > 0
              ? question.options
              : Array.isArray(question?.buttons) && question.buttons.length > 0
                ? question.buttons
                : ['YES', 'NO'];
            const promptText =
              question?.text ||
              question?.displayText ||
              question?.message ||
              question?.prompt ||
              question?.status ||
              'Please respond to the terminal prompt.';
            setTyroPrompt({
              text: promptText,
              options,
              answerCallback,
              awaitingResult: false,
            });
          }
        : undefined,
      statusMessageCallback: (message) => {
        console.info('Tyro refund status', message);
      },
      receiptCallback: (receipt) => {
        console.debug('[TyroRefund] receiptCallback payload', receipt);
        recordReceiptPayload(receipt);
      },
      transactionCompleteCallback: (result) => {
        recordReceiptPayload({
          customerReceipt: result?.customerReceipt,
          merchantReceipt: result?.merchantReceipt,
          receipts: result?.receipts,
        });
        if (result.result === 'APPROVED') {
          const hasReceipts =
            receiptsRef.current.customerReceipt || receiptsRef.current.merchantReceipt;
          onRefundSuccess(
            result.transactionId,
            hasReceipts ? { ...receiptsRef.current } : null
          );
          resetReceipts();
        } else {
          onRefundError(result.errorReason);
          resetReceipts();
        }
        setTyroPrompt(null);
        setProcessing(false);
      },
    };

    try {
      tyro.initiateRefund(requestParams, transactionCallbacks);
    } catch (err) {
      onRefundError(err.message);
      setProcessing(false);
      setTyroPrompt(null);
    }
  };

  const handleTyroPromptAnswer = (option) => {
    if (!tyroPrompt) return;
    try {
      tyroPrompt.answerCallback(option);
      setTyroPrompt((prev) => (prev ? { ...prev, awaitingResult: true, selectedOption: option } : null));
    } catch (err) {
      console.error('Failed to send Tyro answer', err);
      setTyroPrompt(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-3 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">{title}</h2>
        {subtitle && (
          <p className="text-sm text-gray-600">{subtitle}</p>
        )}
        <p className="text-center font-bold text-4xl my-6">${total.toFixed(2)}</p>
        
        {!tyroConfig?.integrationKey || !tyroConfig?.tid ? (
          <p className="text-center text-sm text-red-500">
            Tyro terminal not paired. Select a terminal in Terminal Setup and pair it in Settings -> Tyro.
          </p>
        ) : null}

        <button
          onClick={handleRefund}
          disabled={!tyro || processing || !tyroConfig?.integrationKey || !tyroConfig?.tid}
          className="w-full mt-4 bg-secondary text-white py-2 rounded-md font-semibold hover:bg-secondary-dark disabled:bg-gray-400"
        >
          {processing ? 'Processing...' : `Refund ${total.toFixed(2)} with Tyro`}
        </button>
        
        {!disableCancel && (
          <button onClick={onCancel} className="w-full mt-2 text-center text-gray-600 py-2">Cancel</button>
        )}
        {disableCancel && (
          <p className="mt-3 text-xs text-amber-700 text-center">
            Complete all remaining Tyro legs to finish this split refund.
          </p>
        )}
      </div>

      {tyroConfig?.useHeadlessMode !== false && tyroPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto p-4 sm:p-6 space-y-4">
            <h3 className="text-lg font-semibold">Tyro Question</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{tyroPrompt.text}</p>
            {tyroPrompt.awaitingResult && (
              <p className="text-xs text-gray-500">
                Response sent ({tyroPrompt.selectedOption || '...'}). Waiting for terminal to continue.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tyroPrompt.options.map((option) => (
                <button
                  key={option}
                  onClick={() => handleTyroPromptAnswer(option)}
                  disabled={tyroPrompt.awaitingResult}
                  className={`px-4 py-2 rounded-md border border-primary text-primary hover:bg-primary hover:text-white ${
                    tyroPrompt.awaitingResult ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TyroRefundModal;
